import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import {
  X,
  Building2,
  BookOpen,
  GraduationCap,
  User,
  Calendar,
  Users,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Trash2,
} from "lucide-react";

const STATUS_LABEL = {
  pending_hq: "Chờ duyệt chuyên môn",
  pending_mgmt: "Chờ duyệt lãnh đạo",
  completed: "Hoàn thành",
  rejected: "Từ chối",
};

const CertificateRequestDetailModal = ({
  open,
  detail,
  busy = false,
  templateOptions = [],
  onClose,
  onApproveHq,
  onApproveMgmt,
  onReject,
  onDelete,
  canApproveHq = false,
  canApproveMgmt = false,
  canDeleteRequest = false,
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const isTeacher = detail?.request_kind === "teacher";
  const canPickTemplate = open && detail?.status === "pending_hq" && !isTeacher;
  const studentTemplates = useMemo(() => templateOptions || [], [templateOptions]);
  const canShowFooterActions =
    canDeleteRequest ||
    (detail?.status === "pending_hq" && canApproveHq) ||
    (detail?.status === "pending_mgmt" && canApproveMgmt);
  const infoSectionRef = useRef(null);
  const approvalSectionRef = useRef(null);
  const [rightGroupHeight, setRightGroupHeight] = useState(null);

  const splitName = (fullName) => {
    const cleaned = String(fullName || "").trim().replace(/\s+/g, " ");
    if (!cleaned) return { lastName: "", firstName: "" };
    const parts = cleaned.split(" ");
    const firstName = parts.pop() || "";
    const lastName = parts.join(" ");
    return { lastName, firstName };
  };

  const sortedItems = useMemo(() => {
    const rows = Array.isArray(detail?.items) ? [...detail.items] : [];
    rows.sort((a, b) => {
      const an = splitName(a?.full_name || a?.teacher_name || "");
      const bn = splitName(b?.full_name || b?.teacher_name || "");
      const byFirst = an.firstName.localeCompare(bn.firstName, "vi", { sensitivity: "base" });
      if (byFirst !== 0) return byFirst;
      const byLast = an.lastName.localeCompare(bn.lastName, "vi", { sensitivity: "base" });
      if (byLast !== 0) return byLast;
      return String(a?.student_id || a?.teacher_id || "").localeCompare(
        String(b?.student_id || b?.teacher_id || ""),
        "vi",
        { sensitivity: "base" }
      );
    });
    return rows;
  }, [detail?.items]);

  useEffect(() => {
    if (!open) return;
    setSelectedTemplateId(detail?.template_id || studentTemplates[0]?.id || "");
  }, [open, detail?.template_id, studentTemplates]);

  useLayoutEffect(() => {
    if (!open) return;
    const calculate = () => {
      const infoHeight = infoSectionRef.current?.offsetHeight || 0;
      const approvalHeight = approvalSectionRef.current?.offsetHeight || 0;
      if (infoHeight > 0 || approvalHeight > 0) {
        setRightGroupHeight(infoHeight + approvalHeight + 16);
      }
    };
    calculate();
    const infoEl = infoSectionRef.current;
    const approvalEl = approvalSectionRef.current;
    if (typeof ResizeObserver !== "undefined") {
      const obs = new ResizeObserver(calculate);
      if (infoEl) obs.observe(infoEl);
      if (approvalEl) obs.observe(approvalEl);
      window.addEventListener("resize", calculate);
      return () => {
        obs.disconnect();
        window.removeEventListener("resize", calculate);
      };
    }
    window.addEventListener("resize", calculate);
    return () => window.removeEventListener("resize", calculate);
  }, [open, detail, canPickTemplate, studentTemplates.length]);

  if (!open || !detail) return null;

  const infoItem = (Icon, label, value, iconColor = "text-gray-400") => (
    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
      <div className={`p-2 rounded-lg bg-gray-50 ${iconColor}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">{label}</p>
        <p className="text-[13px] font-semibold text-gray-700">{value || "—"}</p>
      </div>
    </div>
  );

  const statusBadgeClass =
    detail.status === "completed"
      ? "bg-emerald-50 border-emerald-100 text-emerald-700"
      : detail.status === "rejected"
      ? "bg-red-50 border-red-100 text-red-700"
      : "bg-amber-50 border-amber-100 text-amber-800";

  const modalContent = (
    <AnimatePresence>
      <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-slate-900/55"
          onClick={onClose}
        />
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          className="relative z-[225] bg-white w-full max-w-6xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        >
          <div className="relative p-6 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-16 h-16 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center text-2xl font-black border border-red-100 shadow-sm">
                {(detail.class_name || "Y").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-gray-800 truncate">
                  {detail.class_name || "Yêu cầu cấp giấy chứng nhận"}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] font-bold uppercase">
                    Chi tiết yêu cầu
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${statusBadgeClass}`}>
                    {STATUS_LABEL[detail.status] || detail.status}
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-full transition-all"
              aria-label="Đóng"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden p-6">
            <div className="h-full min-h-0 grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="min-h-0 overflow-y-auto pr-2 space-y-4">
                <div ref={infoSectionRef}>
                  <h3 className="text-[11px] font-black text-red-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <div className="w-1 h-4 bg-red-500 rounded-full" /> Thông tin yêu cầu
                  </h3>
                  <div className="space-y-1">
                    {infoItem(Building2, "Chi nhánh", detail.branch_name, "text-blue-600")}
                    {infoItem(BookOpen, "Môn học", detail.subject_name, "text-indigo-600")}
                    {infoItem(GraduationCap, "Khóa học", detail.course_name, "text-purple-500")}
                    {infoItem(User, "Người gửi", detail.requester_name, "text-gray-500")}
                    {infoItem(
                      Calendar,
                      "Ngày gửi",
                      detail.created_at ? new Date(detail.created_at).toLocaleString("vi-VN") : "—",
                      "text-gray-500"
                    )}
                    {infoItem(Users, "Số lượng", String(detail.student_count || detail.items?.length || 0), "text-red-600")}
                  </div>
                </div>

                <div ref={approvalSectionRef}>
                  <h3 className="text-[11px] font-black text-red-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <div className="w-1 h-4 bg-red-500 rounded-full" /> Thông tin phê duyệt
                  </h3>
                  <div className="space-y-1 bg-gray-50/50 p-2 rounded-2xl border border-gray-100">
                    {infoItem(BookOpen, "Biểu mẫu chứng nhận", detail.template_name, "text-indigo-600")}
                    {infoItem(ShieldCheck, "Người duyệt cấp 1", detail.hq_approver_name, "text-emerald-600")}
                    {infoItem(
                      Calendar,
                      "Ngày duyệt cấp 1",
                      detail.hq_approved_at ? new Date(detail.hq_approved_at).toLocaleString("vi-VN") : "—",
                      "text-emerald-600"
                    )}
                  </div>
                </div>

                {canPickTemplate && (
                  <div>
                    <h3 className="text-[11px] font-black text-red-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                      <div className="w-1 h-4 bg-red-500 rounded-full" /> Cấu hình duyệt cấp 1
                    </h3>
                    <div className="bg-gray-50/50 p-3 rounded-2xl border border-gray-100">
                      <label className="text-[11px] font-black text-gray-500 uppercase tracking-wider block mb-2">
                        Biểu mẫu giấy chứng nhận
                      </label>
                      <select
                        value={selectedTemplateId}
                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold"
                      >
                        <option value="">Chọn biểu mẫu</option>
                        {studentTemplates.map((tpl) => (
                          <option key={tpl.id} value={tpl.id}>
                            {tpl.name}
                          </option>
                        ))}
                      </select>
                      {studentTemplates.length === 0 && (
                        <p className="mt-2 text-xs font-bold text-amber-700">
                          Chưa có biểu mẫu cho đối tượng học viên. Vui lòng kiểm tra `certificate_templates`.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div
                className="lg:col-span-2 min-h-0 flex flex-col pl-2"
                style={rightGroupHeight ? { height: `${rightGroupHeight}px` } : undefined}
              >
                <h3 className="text-[11px] font-black text-red-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <div className="w-1 h-4 bg-red-500 rounded-full" /> Danh sách chi tiết
                </h3>
                <div className="rounded-2xl border border-gray-100 overflow-hidden flex-1 min-h-0">
                  <div className="h-full overflow-auto">
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-2 text-[10px] font-black text-gray-400 uppercase">#</th>
                          <th className="px-3 py-2 text-[10px] font-black text-gray-400 uppercase">Họ</th>
                          <th className="px-3 py-2 text-[10px] font-black text-gray-400 uppercase">Tên (A-Z)</th>
                          <th className="px-3 py-2 text-[10px] font-black text-gray-400 uppercase text-center">Đầu vào</th>
                          <th className="px-3 py-2 text-[10px] font-black text-gray-400 uppercase text-center">Đầu ra</th>
                          <th className="px-3 py-2 text-[10px] font-black text-gray-400 uppercase">Kết quả</th>
                          <th className="px-3 py-2 text-[10px] font-black text-gray-400 uppercase">Đánh giá</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedItems.map((it, idx) => {
                          const n = splitName(it.full_name || it.teacher_name || "");
                          return (
                          <tr key={it.student_id || it.teacher_id || idx} className="border-b border-gray-50 hover:bg-gray-50/40">
                            <td className="px-3 py-2 text-xs text-gray-500">{idx + 1}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{n.lastName || "—"}</td>
                            <td className="px-3 py-2 text-sm font-bold text-gray-800">{n.firstName || "—"}</td>
                            <td className="px-3 py-2 text-sm text-center">{it.entrance_score ?? "—"}</td>
                            <td className="px-3 py-2 text-sm text-center">{it.exit_score ?? "—"}</td>
                            <td className="px-3 py-2 text-sm font-bold text-gray-800">{it.outcome_text || it.computed_grade || "—"}</td>
                            <td className="px-3 py-2 text-xs text-gray-600">{it.remarks || it.assessment_text || "—"}</td>
                          </tr>
                        )})}
                        {sortedItems.length === 0 && (
                          <tr>
                            <td colSpan={7} className="px-3 py-8 text-center text-sm font-bold text-gray-400">
                              Không có dữ liệu chi tiết.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {canShowFooterActions && (
          <div className="p-6 border-t border-gray-50 bg-gray-50/30 flex flex-wrap items-center justify-between gap-3">
            {canDeleteRequest && (
              <button
                type="button"
                disabled={busy}
                onClick={onDelete}
                className="px-6 py-3 rounded-xl font-bold text-red-800 border border-red-200 bg-white hover:bg-red-50 transition-all disabled:opacity-50"
              >
                <span className="inline-flex items-center gap-2">
                  <Trash2 size={18} /> Xóa yêu cầu
                </span>
              </button>
            )}
            <div className="flex flex-wrap gap-3 justify-end ml-auto">
              {detail.status === "pending_hq" && canApproveHq && (
                <>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={onReject}
                    className="px-6 py-3 rounded-xl font-bold text-gray-700 border border-gray-200 bg-white hover:bg-gray-50 transition-all disabled:opacity-50"
                  >
                    <span className="inline-flex items-center gap-2">
                      <XCircle size={18} /> KHÔNG DUYỆT
                    </span>
                  </button>
                  <button
                    type="button"
                    disabled={busy || (canPickTemplate && !selectedTemplateId)}
                    onClick={() => onApproveHq(selectedTemplateId)}
                    className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg shadow-emerald-100 transition-all active:scale-95 disabled:opacity-50"
                  >
                    <CheckCircle2 size={18} /> DUYỆT CẤP 1
                  </button>
                </>
              )}
              {detail.status === "pending_mgmt" && canApproveMgmt && (
                <>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={onReject}
                    className="px-6 py-3 rounded-xl font-bold text-gray-700 border border-gray-200 bg-white hover:bg-gray-50 transition-all disabled:opacity-50"
                  >
                    <span className="inline-flex items-center gap-2">
                      <XCircle size={18} /> KHÔNG DUYỆT
                    </span>
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={onApproveMgmt}
                    className="px-8 py-3 bg-red-700 hover:bg-red-800 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg shadow-red-100 transition-all active:scale-95 disabled:opacity-50"
                  >
                    <CheckCircle2 size={18} /> DUYỆT CẤP 2
                  </button>
                </>
              )}
            </div>
          </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );

  if (typeof document !== "undefined") {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};

export default CertificateRequestDetailModal;

